import { cn } from '@/lib/utils';

import {
  FOCUS_SESSIONS_PER_LONG_BREAK,
  type PomodoroSessionType,
} from './pomodoro-store';

/** The full cycle laid out step by step: F sb F sb F sb F LB. */
const CYCLE_STEPS = Array.from({ length: FOCUS_SESSIONS_PER_LONG_BREAK }).flatMap(
  (_, i): PomodoroSessionType[] => [
    'focus',
    i < FOCUS_SESSIONS_PER_LONG_BREAK - 1 ? 'shortBreak' : 'longBreak',
  ],
);

interface CycleDotsProps {
  /** The RUNNING/committed session (drives the dot position), not the preview. */
  sessionType: PomodoroSessionType;
  completedFocus: number;
  running: boolean;
  className?: string;
}

/**
 * The Pomodoro cycle laid out as dots — big dot = focus, small dot = short
 * break, pill = long break. Steps AT or BEFORE the timer's position are tinted
 * by their OWN type (focus violet: primary-300 done / primary-500 current;
 * breaks grey: shortBreak secondary-400, longBreak secondary-600); upcoming
 * steps are faint (secondary-200). The current dot pulses while running. `completedFocus`
 * resets after each long break. Shared by the dashboard tile and the floating
 * panel so both surfaces render the identical row.
 */
export function CycleDots({
  sessionType,
  completedFocus,
  running,
  className,
}: CycleDotsProps) {
  // Linear position of the timer in CYCLE_STEPS: focus n sits at index 2n; the
  // break after it at 2n+1 (completedFocus is already n+1 by then, hence the -1).
  const pos = sessionType === 'focus' ? completedFocus * 2 : completedFocus * 2 - 1;
  return (
    <div
      className={cn('flex items-center justify-center gap-1.5', className)}
      aria-hidden>
      {CYCLE_STEPS.map((step, i) => {
        // Upcoming (after the timer): faint. Reached (at/before): tinted by the
        // step's OWN type — the current step gets the fuller shade.
        const color =
          i > pos
            ? 'bg-secondary-200'
            : step === 'focus'
              ? i === pos
                ? 'bg-primary-500'
                : 'bg-primary-300'
              : step === 'shortBreak'
                ? 'bg-secondary-400'
                : 'bg-secondary-600';
        return (
          <span
            key={i}
            className={cn(
              'rounded-full',
              step === 'focus'
                ? 'h-2 w-2'
                : step === 'shortBreak'
                  ? 'h-1 w-1'
                  : 'h-1.5 w-3',
              color,
              i === pos && running && 'animate-pulse',
            )}
          />
        );
      })}
    </div>
  );
}
